import React, { useRef, useEffect, useCallback, useContext } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import {
  TextFieldBehaviourLog,
  CopyTextEventLog,
  PasteTextEventLog,
  AttemptBehaviourLog,
  InactivityLog,
} from '@scale/shared/types/api/complete';
import { ReviewLevel } from '@scale/shared-types';
import { bufferDebounceTime } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useAutoTyperDetector';
import produce from 'immer';
import _ from 'lodash';
import { Subject } from 'rxjs';
import useStorageRef from '../hooks/useStorageRef';

interface TypingEvent {
  fieldId: string; // e.g. promptText, responseText
  text: string; // the latest text value
  timestamp: number; // event timestamp
}

const RESPONSE_INDEX_DELIMITER = '__RESPONSE_INDEX__';
const SUFFIX_DELIMITER = '__SUFFIX__';
const ACTIVE_TYPING_TIME_UPDATE_DEBOUNCE_MS = 3000;

interface TextFieldData {
  latestValue: string;
  activeTimeTyping: number;
  lengthDiffLog: number[];
  lengthDiffTimestamps: number[];
  numAutotyperDetected: number;
  numMouseLeaves: number;
  longDiffTexts: string[];
  pasteEvents: CopyOrPasteEvent[];
}

type TextFieldDataMap = {
  [fieldId: string]: TextFieldData;
};

interface CopyOrPasteEvent {
  text: string;
  timestamp: number;
}

enum PresenceEventType {
  // mouse enters the component area
  MouseEnter = 'mouseenter',
  // mouse leaves the component area
  MouseLeave = 'mouseleave',
  // document.hidden turns to false
  Visible = 'visible',
  // document.hidden turns to true
  Hidden = 'hidden',
  // the hook was initialized
  Load = 'load',
}

interface PresenceEvent {
  type: PresenceEventType;
  timestamp: number;
}

const ACTIVE_EVENTS = [PresenceEventType.MouseEnter, PresenceEventType.Visible];
const INACTIVE_EVENTS = [PresenceEventType.MouseLeave, PresenceEventType.Hidden];

interface AttemptBehaviorLogContextProviderProps {
  projectId?: string;
  workerId?: string;
  assignmentId?: string;
  reviewLevel?: ReviewLevel;
  annotations?: any;
  children?: React.ReactNode;
}

interface InitializationState {
  [key: string]: boolean;
}

const LONG_DIFF_TEXT_THRESHOLD = 100;

const getFieldIdWithIndex = (fieldId: string, responseIndex?: number, suffix?: string) => {
  return `${fieldId}${
    responseIndex !== undefined ? `${RESPONSE_INDEX_DELIMITER}${responseIndex}` : ''
  }${suffix ? `${SUFFIX_DELIMITER}${suffix}` : ''}`;
};

const useContextValue = (props: AttemptBehaviorLogContextProviderProps) => {
  const typingEvent$ = useRef(new Subject<TypingEvent>());

  const { projectId, workerId, assignmentId, reviewLevel } = props;

  const [getTextFieldData, setTextFieldData, subscribeTextFieldData] =
    useStorageRef<TextFieldDataMap>(`textFieldData-${workerId}-${assignmentId}`, {});

  const [getCopyEventsLog, setCopyEventsLog, subscribeCopyEventsLog] = useStorageRef<
    CopyOrPasteEvent[]
  >(`copyEventsLog-${workerId}-${assignmentId}`, []);

  const [getPasteEventsLog, setPasteEventsLog, subscribePasteEventsLog] = useStorageRef<
    CopyOrPasteEvent[]
  >(`pasteEventsLog-${workerId}-${assignmentId}`, []);

  const [getDragAndDropEventsLog, setDragAndDropEventsLog, subscribeDragAndDropEventsLog] =
    useStorageRef<CopyOrPasteEvent[]>(`dragAndDropEventsLog-${workerId}-${assignmentId}`, []);

  const [getInitializationState, setInitializationState, subscribeInitializationState] =
    useStorageRef<InitializationState>(`initializationState-${workerId}-${assignmentId}`, {});

  // tracking when user's mouse enters and leaves the chat task area
  const [getPresenceEventsLog, setPresenceEventsLog, subscribePresenceEventsLog] = useStorageRef<
    PresenceEvent[]
  >(`presenceLog-${workerId}-${assignmentId}`, []);

  const logged = useRef(false);

  useEffect(() => {
    logged.current = false;
  }, [workerId, assignmentId]);

  const emitTypingEvent = useCallback(
    (prop: { fieldId: string; text: string; responseIndex?: number; suffix?: string }) => {
      const { fieldId, text, responseIndex, suffix } = prop;
      const fieldIdWithTurnIndex = getFieldIdWithIndex(fieldId, responseIndex, suffix);
      const timestamp = Date.now();
      typingEvent$.current.next({
        fieldId: fieldIdWithTurnIndex,
        timestamp,
        text,
      });
    },
    [],
  );

  useEffect(() => {
    const subscription = typingEvent$.current
      .asObservable()
      .pipe(bufferDebounceTime(ACTIVE_TYPING_TIME_UPDATE_DEBOUNCE_MS))
      .subscribe(typingEvents => {
        setTextFieldData(prev =>
          produce(prev, draft => {
            const typingEventsGroupedByFieldId = _.groupBy(typingEvents, 'fieldId');
            for (const fieldId in typingEventsGroupedByFieldId) {
              if (!draft[fieldId]) {
                draft[fieldId] = {
                  latestValue: props.annotations?.[fieldId]?.response?.[0] ?? '',
                  lengthDiffLog: [],
                  lengthDiffTimestamps: [],
                  activeTimeTyping: 0,
                  numAutotyperDetected: 0,
                  numMouseLeaves: 0,
                  longDiffTexts: [],
                  pasteEvents: [],
                };
              }

              const typingEventsForField = typingEventsGroupedByFieldId[fieldId];

              // Active typing time calculation
              const timestamps = typingEventsForField.map(event => event.timestamp);
              const activeTypingTimeMs = (_.last(timestamps) ?? 0) - (_.first(timestamps) ?? 0);
              draft[fieldId].activeTimeTyping += activeTypingTimeMs;

              for (const typingEvent of typingEventsForField) {
                const prevText =
                  draft[fieldId]?.latestValue ?? props.annotations?.[fieldId]?.response?.[0] ?? '';

                const lengthDiff = typingEvent.text.length - prevText.length;
                if (lengthDiff >= LONG_DIFF_TEXT_THRESHOLD) {
                  draft[fieldId].longDiffTexts.push(typingEvent.text);
                }

                draft[fieldId].latestValue = typingEvent.text;

                if (Array.isArray(draft[fieldId].lengthDiffLog)) {
                  draft[fieldId].lengthDiffLog.push(lengthDiff);
                } else {
                  draft[fieldId].lengthDiffLog = [lengthDiff];
                }

                if (Array.isArray(draft[fieldId].lengthDiffTimestamps)) {
                  draft[fieldId].lengthDiffTimestamps.push(typingEvent.timestamp);
                } else {
                  draft[fieldId].lengthDiffTimestamps = [typingEvent.timestamp];
                }
              }
            }
          }),
        );
      });
    return () => {
      subscription.unsubscribe();
    };
  }, [props.annotations, setTextFieldData]);

  const setLatestValue = useCallback(
    (prop: {
      fieldId: string;
      text: string;
      responseIndex?: number;
      overrideForField?: boolean;
    }) => {
      if (
        getInitializationState()[getFieldIdWithIndex(prop.fieldId, prop.responseIndex)] &&
        !prop.overrideForField
      ) {
        return;
      }
      const { fieldId, text, responseIndex } = prop;
      const fieldIdWithTurnIndex = getFieldIdWithIndex(fieldId, responseIndex);
      setTextFieldData(prev =>
        produce(prev, draft => {
          draft[fieldIdWithTurnIndex] = {
            latestValue: text,
            lengthDiffLog: [],
            lengthDiffTimestamps: [],
            activeTimeTyping: 0,
            numAutotyperDetected: 0,
            numMouseLeaves: 0,
            longDiffTexts: [],
            pasteEvents: [],
          };
        }),
      );
      setInitializationState(prev => ({ ...prev, [fieldIdWithTurnIndex]: true }));
    },
    [setTextFieldData, setInitializationState, getInitializationState],
  );

  const handleCopyTextEvent = useCallback(
    (event: ClipboardEvent) => {
      const selectedText = window.getSelection()?.toString();
      if (
        selectedText &&
        event.target instanceof Element &&
        (!['textarea', 'input'].includes(event.target.tagName.toLowerCase()) ||
          ['65a03220febeea7f87d7148f', '651c671f68dd54c040f0342f'].includes(projectId ?? ''))
      ) {
        const timestamp = Date.now();
        setCopyEventsLog(prev => [...prev, { text: selectedText, timestamp }]);
      }
    },
    [setCopyEventsLog, projectId],
  );

  const handlePasteEvent = useCallback(
    (event: ClipboardEvent) => {
      if (event.clipboardData) {
        const pastedText = event.clipboardData.getData('text/plain');
        const timestamp = Date.now();

        // Attempt-level paste tracking
        setPasteEventsLog(prev => [...prev, { text: pastedText, timestamp }]);

        // Field-level paste tracking - try to identify which field was pasted into
        const target = event.target as HTMLElement;
        let fieldId: string | null = null;
        let currentElement: HTMLElement | null = target;

        // Walk up the DOM tree to find data-field-id or data-field-key attributes
        let maxDepth = 15;
        while (currentElement && !fieldId && maxDepth-- > 0) {
          const dataFieldId = currentElement.getAttribute('data-field-id');
          const dataFieldKey = currentElement.getAttribute('data-field-key');
          fieldId = dataFieldId || dataFieldKey;

          if (!fieldId) {
            currentElement = currentElement.parentElement;
          }
        }

        // If we found a field ID, track the paste event for that field
        if (fieldId && pastedText) {
          const validFieldId = fieldId;
          setTextFieldData(prev =>
            produce(prev, draft => {
              if (!draft[validFieldId]) {
                draft[validFieldId] = {
                  latestValue: props.annotations?.[validFieldId]?.response?.[0] ?? '',
                  lengthDiffLog: [],
                  lengthDiffTimestamps: [],
                  activeTimeTyping: 0,
                  numAutotyperDetected: 0,
                  numMouseLeaves: 0,
                  longDiffTexts: [],
                  pasteEvents: [],
                };
              }
              draft[validFieldId].pasteEvents.push({ text: pastedText, timestamp });
            }),
          );
        }
      }
    },
    [setPasteEventsLog, setTextFieldData, props.annotations],
  );

  const handleDragAndDropEvent = useCallback(
    (event: DragEvent) => {
      if (event.dataTransfer) {
        const pastedText = event.dataTransfer.getData('text/plain');
        const timestamp = Date.now();
        setDragAndDropEventsLog(prev => [
          ...prev,
          {
            text: pastedText,
            timestamp,
          },
        ]);
      }
    },
    [setDragAndDropEventsLog],
  );

  const getInactivityLog = useCallback((): InactivityLog => {
    const actions = getPresenceEventsLog();
    // calculating intervals between mouseleave and mouseenter events
    // or between component became hidden and visible again
    const outOfPageTimeArray = actions.reduce((acc: number[], action, index) => {
      if (
        INACTIVE_EVENTS.includes(action.type) &&
        ACTIVE_EVENTS.includes(actions[index + 1]?.type)
      ) {
        if (!actions[index + 1].timestamp) return acc;
        acc.push((actions[index + 1].timestamp - action.timestamp) / 1000);
      }
      return acc;
    }, []);

    return {
      totalOutOfPageTimeSecs: _.sum(outOfPageTimeArray) ?? 0,
      numOutOfPageEvents: outOfPageTimeArray.length ?? 0,
      maxOutOfPageTimeSecs: _.max(outOfPageTimeArray),
      meanOutOfPageTimeSecs: _.mean(outOfPageTimeArray),
    };
  }, [getPresenceEventsLog]);

  const hasActivityLoggingStarted = useRef(false);

  const getPresenceEventType = useCallback((event: Event): PresenceEventType => {
    if (event.type === 'mouseenter') return PresenceEventType.MouseEnter;
    if (event.type === 'mouseleave') return PresenceEventType.MouseLeave;
    if (event.type === 'visibilitychange') {
      return document.hidden ? PresenceEventType.Hidden : PresenceEventType.Visible;
    }
    return PresenceEventType.MouseEnter;
  }, []);

  const handlePresenceEvent = useCallback(
    (event: Event) => {
      const timestamp = Date.now();
      // This helps to avoid situation when user left tab, closed it,
      // Opened task again and it's counted as inactivity time
      if (!hasActivityLoggingStarted.current) {
        hasActivityLoggingStarted.current = true;
        setPresenceEventsLog(prev => [...prev, { type: PresenceEventType.Load, timestamp }]);
      }
      const type = getPresenceEventType(event);
      setPresenceEventsLog(prev => [...prev, { type, timestamp }]);
    },
    [setPresenceEventsLog, getPresenceEventType],
  );

  useEffect(() => {
    document.addEventListener('copy', handleCopyTextEvent);
    document.addEventListener('paste', handlePasteEvent);
    document.addEventListener('drop', handleDragAndDropEvent);
    document.addEventListener('mouseenter', handlePresenceEvent);
    document.addEventListener('mouseleave', handlePresenceEvent);
    document.addEventListener('visibilitychange', handlePresenceEvent);
    return () => {
      document.removeEventListener('copy', handleCopyTextEvent);
      document.removeEventListener('paste', handlePasteEvent);
      document.removeEventListener('drop', handleDragAndDropEvent);
      document.removeEventListener('mouseenter', handlePresenceEvent);
      document.removeEventListener('mouseleave', handlePresenceEvent);
      document.removeEventListener('visibilitychange', handlePresenceEvent);
    };
  }, [handleCopyTextEvent, handlePasteEvent, handleDragAndDropEvent, handlePresenceEvent]);

  const incAutotyperDetectedEvent = useCallback(
    (sub_props: { fieldId: string; responseIndex?: number; suffix?: string }) => {
      const { fieldId, responseIndex, suffix } = sub_props;
      const fieldIdWithIndex = getFieldIdWithIndex(fieldId, responseIndex, suffix);
      setTextFieldData(prev =>
        produce(prev, draft => {
          if (!draft[fieldIdWithIndex]) {
            draft[fieldIdWithIndex] = {
              latestValue: props.annotations?.[fieldIdWithIndex]?.response?.[0] ?? '',
              lengthDiffLog: [],
              lengthDiffTimestamps: [],
              activeTimeTyping: 0,
              numAutotyperDetected: 0,
              numMouseLeaves: 0,
              longDiffTexts: [],
              pasteEvents: [],
            };
          }
          draft[fieldIdWithIndex].numAutotyperDetected =
            draft[fieldIdWithIndex].numAutotyperDetected + 1;
        }),
      );
    },
    [props.annotations, setTextFieldData],
  );

  const incMouseLeaveEvent = useCallback(
    (sub_props: { fieldId: string; responseIndex?: number; suffix?: string }) => {
      const { fieldId, responseIndex, suffix } = sub_props;
      const fieldIdWithIndex = getFieldIdWithIndex(fieldId, responseIndex, suffix);
      setTextFieldData(prev =>
        produce(prev, draft => {
          if (!draft[fieldIdWithIndex]) {
            draft[fieldIdWithIndex] = {
              latestValue: props.annotations?.[fieldIdWithIndex]?.response?.[0] ?? '',
              lengthDiffLog: [],
              lengthDiffTimestamps: [],
              activeTimeTyping: 0,
              numAutotyperDetected: 0,
              numMouseLeaves: 0,
              longDiffTexts: [],
              pasteEvents: [],
            };
          }
          draft[fieldIdWithIndex].numMouseLeaves = draft[fieldIdWithIndex].numMouseLeaves + 1;
        }),
      );
    },
    [props.annotations, setTextFieldData],
  );

  const calculatePasteStatistics = useCallback((pasteEvents: CopyOrPasteEvent[]) => {
    const pasteTextLog = pasteEvents.map(event => event.text);
    const pasteTimestampLog = pasteEvents.map(event => event.timestamp);

    return {
      pasteTextLog,
      pasteTimestampLog,
      maxPasteTextLength:
        pasteTextLog.length > 0 ? _.maxBy(pasteTextLog, text => text.length)?.length ?? 0 : 0,
      minPasteTextLength:
        pasteTextLog.length > 0 ? _.minBy(pasteTextLog, text => text.length)?.length ?? 0 : 0,
      avgPasteTextLength:
        pasteTextLog.length > 0 ? _.meanBy(pasteTextLog, text => text?.length ?? 0) : 0,
      numPasteEvents: pasteTextLog.length,
    };
  }, []);

  // eslint-disable-next-line complexity
  const getAttemptBehaviourLog = useCallback((): AttemptBehaviourLog => {
    const textFieldData = getTextFieldData();
    const fieldIds = _.uniq(Object.keys(textFieldData));

    const attemptBehaviourLog: Record<string, TextFieldBehaviourLog> = {};

    for (const fieldId of fieldIds) {
      const {
        latestValue: textValue,
        lengthDiffLog,
        lengthDiffTimestamps,
        numMouseLeaves,
        activeTimeTyping,
        numAutotyperDetected,
        longDiffTexts,
        pasteEvents = [],
      } = textFieldData[fieldId];

      const backspaceArr = lengthDiffLog
        .filter((diff: number) => diff < 0)
        .map((diff: number) => -diff);

      const pasteTextArr = lengthDiffLog.filter((diff: number) => diff > 1);

      // Calculate field-level paste event statistics
      const fieldPasteStats = calculatePasteStatistics(pasteEvents);

      attemptBehaviourLog[fieldId] = {
        numBackspaces: backspaceArr.length,
        avgBackspaceLength: _.mean(backspaceArr),
        maxBackspaceLength: _.max(backspaceArr),
        numOfCopyPaste: pasteTextArr.length,
        avgCopyPasteLength: _.mean(pasteTextArr),
        maxCopyPasteLength: _.max(pasteTextArr),
        activeTimeTyping,
        totalChanges: lengthDiffLog.length,
        numAutotyperDetected,
        numMouseLeaves,
        lengthDiffLog,
        lengthDiffTimestamps,
        charCount: textValue.length,
        wordCount: _.words(textValue).length,
        longDiffTexts,
        // Field-level paste events
        ...fieldPasteStats,
      };
    }

    const copyEventsLog = getCopyEventsLog();
    const copyTextLog = copyEventsLog?.map(event => event.text) ?? [];
    const copyTimestampLog = copyEventsLog?.map(event => event.timestamp) ?? [];
    const copyTextEventLog: CopyTextEventLog = {
      copyTextLog,
      copyTimestampLog,
      maxCopyTextLength:
        copyTextLog.length > 0
          ? _.maxBy(copyTextLog, (text: string) => text.length)?.length ?? 0
          : 0,
      minCopyTextLength:
        copyTextLog.length > 0
          ? _.minBy(copyTextLog, (text: string) => text.length)?.length ?? 0
          : 0,
      avgCopyTextLength:
        copyTextLog.length > 0 ? _.meanBy(copyTextLog, text => text?.length ?? 0) : 0,
      numCopyEvents: copyTextLog.length,
    };

    const pasteEventsLog = getPasteEventsLog();
    const pasteTextLog = pasteEventsLog?.map(event => event.text) ?? [];
    const pasteTimestampLog = pasteEventsLog?.map(event => event.timestamp) ?? [];
    const pasteTextEventLog: PasteTextEventLog = {
      pasteTextLog,
      pasteTimestampLog,
      maxPasteTextLength:
        pasteTextLog.length > 0
          ? _.maxBy(pasteTextLog, (text: string) => text.length)?.length ?? 0
          : 0,
      minPasteTextLength:
        pasteTextLog.length > 0
          ? _.minBy(pasteTextLog, (text: string) => text.length)?.length ?? 0
          : 0,
      avgPasteTextLength:
        pasteTextLog.length > 0 ? _.meanBy(pasteTextLog, text => text?.length ?? 0) : 0,
      numPasteEvents: pasteTextLog.length,
    };

    const dragAndDropEventsLog = getDragAndDropEventsLog();
    const dragAndDropTextLog = dragAndDropEventsLog?.map(event => event.text) ?? [];
    const dragAndDropTimestampLog = dragAndDropEventsLog?.map(event => event.timestamp) ?? [];
    const dragAndDropTextEventsLog: PasteTextEventLog = {
      pasteTextLog: dragAndDropTextLog,
      pasteTimestampLog: dragAndDropTimestampLog,
      maxPasteTextLength:
        dragAndDropTextLog.length > 0
          ? _.maxBy(dragAndDropTextLog, (text: string) => text.length)?.length ?? 0
          : 0,
      minPasteTextLength:
        dragAndDropTextLog.length > 0
          ? _.minBy(dragAndDropTextLog, (text: string) => text.length)?.length ?? 0
          : 0,
      avgPasteTextLength:
        dragAndDropTextLog.length > 0 ? _.meanBy(dragAndDropTextLog, text => text?.length ?? 0) : 0,
      numPasteEvents: dragAndDropTextLog.length,
    };

    const inactivityLog = getInactivityLog();

    return {
      textFields: attemptBehaviourLog,
      copyTextEvents: copyTextEventLog,
      pasteTextEvents: pasteTextEventLog,
      dragAndDropEvents: dragAndDropTextEventsLog,
      inactivityLog,
    };
  }, [
    getTextFieldData,
    getCopyEventsLog,
    getPasteEventsLog,
    getDragAndDropEventsLog,
    getInactivityLog,
    calculatePasteStatistics,
  ]);

  // Log the attempt behaviour event to events table. This should be called upon task submission
  const logBehaviorEvent = useCallback(() => {
    if (logged.current) return;
    logged.current = true;
    const behaviorLog = getAttemptBehaviourLog();
    const eventData = {
      ...behaviorLog,
      projectId,
      workerId,
      assignmentId,
      reviewLevel,
    };
    return eventData;
  }, [logged, projectId, workerId, assignmentId, reviewLevel, getAttemptBehaviourLog]);

  return {
    emitTypingEvent,
    incAutotyperDetectedEvent,
    incMouseLeaveEvent,
    logBehaviorEvent,
    getAttemptBehaviourLog,
    setLatestValue,
    getTextFieldData,
    getCopyEventsLog,
    getPasteEventsLog,
    getDragAndDropEventsLog,
    getInitializationState,
    subscribeTextFieldData,
    subscribeCopyEventsLog,
    subscribePasteEventsLog,
    subscribeDragAndDropEventsLog,
    subscribeInitializationState,
    setTextFieldData,
    setCopyEventsLog,
    setPasteEventsLog,
    setDragAndDropEventsLog,
    setInitializationState,
    setPresenceEventsLog,
    getPresenceEventsLog,
    subscribePresenceEventsLog,
  };
};

type AttemptBehaviorLogContextValue = ReturnType<typeof useContextValue>;

export const AttemptBehaviorLogContext = React.createContext<AttemptBehaviorLogContextValue>({
  emitTypingEvent: (_prop: { fieldId: string; text: string; turnIndex?: number }) => {},
  incAutotyperDetectedEvent: (_prop: { fieldId: string; turnIndex?: number }) => {},
  incMouseLeaveEvent: (_prop: { fieldId: string; turnIndex?: number }) => {},
  logBehaviorEvent: () => {},
  getAttemptBehaviourLog: () => ({}),
  setLatestValue: (_prop: { fieldId: string; text: string; turnIndex?: number }) => {},

  // TODO: @Lajos in a follow-up fix this typescript type narrowing issue
  // NOTE: Please ignore these comments for now!
  // getTextFieldData: () => {},
  // getCopyEventsLog: () => [],
  // getPasteEventsLog: () => [],
  // getInitializationState: () => {},
  // subscribeTextFieldData: () => () => false,
  // subscribeCopyEventsLog: () => () => false,
  // subscribePasteEventsLog: () => () => false,
  // subscribeInitializationState: () => () => false,
  // setTextFieldData: () => {},
  // setCopyEventsLog: () => {},
  // setPasteEventsLog: () => {},
  // setInitializationState: () => {},
} as ReturnType<typeof useContextValue>);

export const useAttemptBehaviorLog = () => {
  return useContext(AttemptBehaviorLogContext);
};

export const useAttemptBehaviorTextFieldData = () => {
  const { getTextFieldData, subscribeTextFieldData, setTextFieldData } = useAttemptBehaviorLog();
  const textFieldData = useSyncExternalStore(subscribeTextFieldData, getTextFieldData);
  return [textFieldData, setTextFieldData];
};
export const useAttemptBehaviorCopyEventsLog = () => {
  const { getCopyEventsLog, subscribeCopyEventsLog, setCopyEventsLog } = useAttemptBehaviorLog();
  const copyEventsLog = useSyncExternalStore(subscribeCopyEventsLog, getCopyEventsLog);
  return [copyEventsLog, setCopyEventsLog];
};
export const useAttemptBehaviorPasteEventsLog = () => {
  const { getPasteEventsLog, subscribePasteEventsLog, setPasteEventsLog } = useAttemptBehaviorLog();
  const pasteEventsLog = useSyncExternalStore(subscribePasteEventsLog, getPasteEventsLog);
  return [pasteEventsLog, setPasteEventsLog];
};
export const useAttemptBehaviorInitializationState = () => {
  const { getInitializationState, subscribeInitializationState, setInitializationState } =
    useAttemptBehaviorLog();
  const initializationState = useSyncExternalStore(
    subscribeInitializationState,
    getInitializationState,
  );
  return [initializationState, setInitializationState];
};
export const useAttemptBehaviorPresenceEventsLog = () => {
  const { getPresenceEventsLog, setPresenceEventsLog, subscribePresenceEventsLog } =
    useAttemptBehaviorLog();
  const presenceEventsLog = useSyncExternalStore(subscribePresenceEventsLog, getPresenceEventsLog);
  return [presenceEventsLog, setPresenceEventsLog];
};

export const AttemptBehaviorLogContextProvider: React.FC<
  AttemptBehaviorLogContextProviderProps
> = ({ children, ...props }) => {
  const contextValue = useContextValue(props);

  return (
    <AttemptBehaviorLogContext.Provider value={contextValue}>
      {children}
    </AttemptBehaviorLogContext.Provider>
  );
};
